/** Vibrance adjustment — pure math + types, no React, no Fabric.
 *
 *  Two sliders, both -100..+100:
 *
 *  - `saturation`: uniform HSL S scale, identical in shape to the
 *    saturation slider in Hue/Saturation. Affects every pixel
 *    equally regardless of how saturated it already is.
 *  - `vibrance`: smart saturation that protects already-saturated
 *    pixels via a quadratic `(1 − S)²` weighting — dull pixels get
 *    most of the boost, vivid pixels barely move. The protective
 *    falloff is what distinguishes Vibrance from Saturation in
 *    Photoshop and Lightroom workflows; preserves existing
 *    contrast in colourful regions while reviving flat ones.
 *
 *  Both apply to the same HSL S; the math is hand-inlined like
 *  Hue/Saturation to keep the per-pixel cost down. Skin-tone
 *  protection (Photoshop's hue-targeted attenuation) is deferred
 *  until a user asks. */

export interface VibranceValue {
  vibrance: number;
  saturation: number;
}

export const identityVibranceValue = (): VibranceValue => ({
  vibrance: 0,
  saturation: 0,
});

/** Pure pixel transform — returns a fresh ImageData. Tests use
 *  this variant; the canvas-bound wrapper does the putImageData. */
export function applyVibrancePixels(source: ImageData, value: VibranceValue): ImageData {
  const w = source.width;
  const h = source.height;
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  const vibranceShift = value.vibrance / 100;
  const saturationShift = value.saturation / 100;

  for (let i = 0; i < sd.length; i += 4) {
    const r = sd[i] / 255;
    const g = sd[i + 1] / 255;
    const b = sd[i + 2] / 255;

    // Inlined RGB → HSL.
    let max = r;
    let min = r;
    if (g > max) max = g;
    if (b > max) max = b;
    if (g < min) min = g;
    if (b < min) min = b;
    const lt = (max + min) * 0.5;
    let hu: number;
    let sa: number;
    if (max === min) {
      hu = 0;
      sa = 0;
    } else {
      const d = max - min;
      sa = lt > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) hu = ((g - b) / d + (g < b ? 6 : 0)) * 60;
      else if (max === g) hu = ((b - r) / d + 2) * 60;
      else hu = ((r - g) / d + 4) * 60;
    }

    // Apply vibrance: quadratic falloff protects already-saturated
    // pixels. At sa=0 full boost, at sa=1 zero boost. Negative
    // vibrance just desaturates uniformly (matches Photoshop).
    if (vibranceShift > 0) {
      const room = 1 - sa;
      sa = sa + room * room * vibranceShift;
    } else if (vibranceShift < 0) {
      sa = sa * (1 + vibranceShift);
    }

    // Apply saturation: uniform scale, same shape as Hue/Sat.
    if (saturationShift > 0) sa = sa + (1 - sa) * saturationShift;
    else if (saturationShift < 0) sa = sa * (1 + saturationShift);

    if (sa > 1) sa = 1;
    else if (sa < 0) sa = 0;

    // Inlined HSL → RGB.
    let rr: number;
    let gg: number;
    let bb: number;
    if (sa === 0) {
      rr = lt;
      gg = lt;
      bb = lt;
    } else {
      const q = lt < 0.5 ? lt * (1 + sa) : lt + sa - lt * sa;
      const p = 2 * lt - q;
      const hk = hu / 360;
      let tR = hk + 1 / 3;
      let tG = hk;
      let tB = hk - 1 / 3;
      if (tR < 0) tR += 1;
      else if (tR > 1) tR -= 1;
      if (tG < 0) tG += 1;
      else if (tG > 1) tG -= 1;
      if (tB < 0) tB += 1;
      else if (tB > 1) tB -= 1;
      const qmp = q - p;
      rr =
        tR < 1 / 6
          ? p + qmp * 6 * tR
          : tR < 1 / 2
            ? q
            : tR < 2 / 3
              ? p + qmp * (2 / 3 - tR) * 6
              : p;
      gg =
        tG < 1 / 6
          ? p + qmp * 6 * tG
          : tG < 1 / 2
            ? q
            : tG < 2 / 3
              ? p + qmp * (2 / 3 - tG) * 6
              : p;
      bb =
        tB < 1 / 6
          ? p + qmp * 6 * tB
          : tB < 1 / 2
            ? q
            : tB < 2 / 3
              ? p + qmp * (2 / 3 - tB) * 6
              : p;
    }

    out[i] = (rr * 255 + 0.5) | 0;
    out[i + 1] = (gg * 255 + 0.5) | 0;
    out[i + 2] = (bb * 255 + 0.5) | 0;
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyVibranceToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: VibranceValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyVibrancePixels(source, value), 0, 0);
}
